import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      /*
       * DIVERGENCE IXEZED, 17/08/2026 — sans « once » : le champ doit etre
       * relache a CHAQUE ouverture, pas seulement a la premiere.
       */
      dialog.addEventListener(DialogOpenEvent.eventName, this.#relacherLeChamp, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    /*
     * DIVERGENCE IXEZED, 19/08/2026 — LE PANNEAU DE RECHERCHE NE SE PRECHARGE
     * PLUS TOUT SEUL.
     *
     * Le client : « des qu'on change de page, on fait quelque chose, il y a des
     * enormes lags ».
     *
     * CE QUE FAISAIT CE BLOC. A chaque chargement de page, sans que personne ait
     * touche a la loupe, il lancait DEUX requetes HTML l'une apres l'autre —
     * « #resetSearch » attend la premiere avant de lancer la seconde :
     *
     *     ...?section_id=predictive-search-empty
     *     /search?q=id:...&resources[type]=product&section_id=predictive-search
     *
     * Chacune etait ensuite analysee par « DOMParser » puis fusionnee dans la
     * page. Or l'etat vide est DEJA rendu en Liquid dans la page, par
     * « search-modal » : on rechargeait un balisage qu'on avait deja.
     *
     * Il suffisait d'avoir consulte une fiche produit pour que cela parte : le
     * script de « scripts.liquid » inscrit le produit courant dans l'historique
     * avant meme que ce composant existe.
     *
     * POURQUOI C'EST SANS RISQUE. Le meme chargement est deja branche sur
     * l'OUVERTURE du panneau — « #handleDialogOpen », pose quelques lignes plus
     * haut, avec la meme garde sur l'historique. On ne supprime donc rien : on
     * laisse faire celui qui se declenche quand le visiteur en a besoin.
     * « #emptyStateLoaded » empeche le double chargement.
     *
     * CE QU'ON PERD, exactement : a la toute premiere ouverture de la recherche
     * dans une page, le bloc « Recemment consulte » arrive apres un aller-retour
     * au lieu d'etre deja la. Le panneau n'est jamais vide pour autant — son
     * etat par defaut est rendu par le serveur avec la page.
     */
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    /*
     * DIVERGENCE IXEZED, 17/08/2026 — UN CLIC AILLEURS RELACHE LE CHAMP.
     *
     * Ce gestionnaire redonnait le focus au champ a tout clic non interactif.
     * Deux consequences, toutes deux signalees par le client :
     *
     *  - le contour vert ne partait jamais. « Je clique sur la carte, mais pas
     *    dans la zone de recherche, pour enlever le contour vert » : le clic
     *    rendait la main au champ, donc le contour revenait aussitot. Il fallait
     *    cliquer plusieurs fois pour que ça finisse par prendre ;
     *  - selectionner du texte devenait impossible : un glisser se termine par
     *    un clic, et la selection sautait avec le focus.
     *
     * Le modele voulu par le client est celui de n'importe quel champ : on
     * clique dedans, c'est actif ; on clique ailleurs, ça se relache. On rend
     * donc le focus a la CARTE, qui reste le piege a focus — le relacher vers
     * la page laisserait le clavier sortir du panneau.
     */
    if (!isInteractiveElement) this.#relacherLeChamp();
  };

  /**
   * Porte le focus sur la carte plutot que sur le champ — 17/08/2026.
   *
   * Sert a l'ouverture et a tout clic hors du champ. La carte porte
   * « tabindex="-1" » : elle peut donc recevoir le focus sans entrer dans
   * l'ordre de tabulation, et le panneau reste ferme au clavier.
   */
  #relacherLeChamp = () => {
    const carte = this.closest('dialog');
    if (!carte) return;
    /* Apres l'ouverture, le navigateur applique SA regle de mise au point : on
     * repasse derriere lui, sinon le champ reprend le focus juste apres nous. */
    requestAnimationFrame(() => {
      if (this.contains(document.activeElement) || document.activeElement === carte) carte.focus();
    });
  };

  /**
   * Met a jour la semantique de la liste de suggestions — defaut F7, 17/08/2026.
   *
   * Le champ se declarait « combobox » avec « aria-expanded » fige a « false »,
   * « aria-activedescendant » jamais pose, et le conteneur « listbox » ne
   * contenait aucun element de role « option ». Un lecteur d'ecran n'annonçait
   * donc ni l'ouverture de la liste, ni l'element parcouru a la fleche.
   *
   * Le role est ecrit dans le balisage ; ce qui se passe ici, c'est ce qui
   * CHANGE : l'identifiant de chaque option, et l'etat du champ. Les
   * identifiants sont poses en JavaScript parce que le meme produit peut
   * paraitre dans deux sections — « Recemment consulte(s) » et « Produits » —
   * et que deux elements ne peuvent pas partager un identifiant.
   */
  #majSemantique() {
    const { searchInput } = this.refs;
    if (!searchInput) return;

    const items = this.#allResultsItems ?? [];
    items.forEach((item, index) => {
      if (!item.id) item.id = `ixz-suggestion-${index}`;
      if (!item.getAttribute('role')) item.setAttribute('role', 'option');
      if (!item.hasAttribute('aria-selected')) item.setAttribute('aria-selected', 'false');
    });

    searchInput.setAttribute('aria-expanded', items.length > 0 ? 'true' : 'false');
    if (!items.length) searchInput.removeAttribute('aria-activedescendant');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    /*
     * DIVERGENCE IXEZED, 16/08/2026 — « Ctrl » accepte a cote de « Cmd ».
     * Seul « metaKey » etait teste : le raccourci ne marchait que sur macOS.
     * On empeche aussi l'action par defaut du navigateur, qui envoie « Ctrl+K »
     * dans la barre d'adresse.
     */
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        /*
         * DIVERGENCE IXEZED, 17/08/2026 — « false » plutot que retire.
         * Un element de role « option » doit porter « aria-selected » en
         * permanence : absent, il est annonce comme non selectionnable.
         */
        item.setAttribute('aria-selected', 'false');
      }
    }

    /*
     * DIVERGENCE IXEZED, 17/08/2026 — defaut F7 : le champ dit QUEL element est
     * parcouru. Le focus ne bouge pas — c'est tout l'interet d'un combobox —
     * donc sans « aria-activedescendant » le lecteur d'ecran n'a aucun moyen de
     * savoir que la fleche a change quoi que ce soit. Mesure avant : « null »
     * apres huit fleches bas.
     */
    if (activeItem?.id) {
      this.refs.searchInput.setAttribute('aria-activedescendant', activeItem.id);
    } else {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      /*
       * DIVERGENCE IXEZED, 16/08/2026 — « Echap » ne fait plus tout d'un coup.
       * Deux gestionnaires ecoutaient la meme touche : le panneau se fermait ET
       * la recherche se vidait, sans aucun moyen d'effacer sa requete au clavier
       * en gardant le panneau. Tant qu'il reste du texte, « Echap » l'efface et
       * s'arrete la ; le panneau se ferme au second appui.
       */
      if (this.refs.searchInput?.value) {
        /*
         * « stopPropagation » seul ne suffit pas : une boite de dialogue se
         * ferme sur « Echap » par le navigateur lui-meme, pas par un
         * gestionnaire. Il faut donc aussi annuler l'action par defaut.
         * Mesure du 16/08/2026 : sans « preventDefault », le panneau se fermait
         * quand meme au premier appui.
         */
        event.preventDefault();
        event.stopPropagation();
      }
      this.#resetSearch();
      return;
    }

    /*
     * DIVERGENCE IXEZED, 17/08/2026 — defaut F21 : « Page suiv. » et
     * « Page prec. » font enfin defiler la liste.
     *
     * Le focus reste TOUJOURS dans le champ — c'est le principe d'un combobox —
     * donc aucune touche de defilement n'atteignait la zone de resultats :
     * mesure a 375x500, 224 px a defiler et 0 px parcouru.
     *
     * On ne touche ni a « Debut » ni a « Fin » : dans un champ de saisie, elles
     * portent le curseur au debut et a la fin du texte, et le visiteur en a
     * besoin. Les detourner casserait l'edition pour reparer un defilement que
     * les fleches assurent deja.
     */
    if (event.key === 'PageDown' || event.key === 'PageUp') {
      const zone = this.querySelector('.predictive-search-form__content');
      if (zone instanceof HTMLElement && zone.scrollHeight > zone.clientHeight) {
        event.preventDefault();
        zone.scrollBy({
          top: (event.key === 'PageDown' ? 1 : -1) * Math.round(zone.clientHeight * 0.9),
          behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        });
      }
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      // DIVERGENCE IXEZED, 15/08/2026 — recensee dans docs/01-architecture.md.
      //
      // Le cas « Tab » etait traite comme une fleche : preventDefault, puis
      // deplacement de l'index, dont le setter redonne le focus au champ. La
      // tabulation ne sortait donc JAMAIS du champ de recherche. Mesure avant
      // correction, a 375 comme a 1440 : six appuis sur Tab, six fois le meme
      // element actif, « input.search-input ». « Effacer », « Fermer », les
      // fiches de resultat et « Tout afficher » etaient inatteignables au
      // clavier — la fenetre de recherche etait un cul-de-sac.
      //
      // Le cas est retire, rien de plus : Tab retrouve son role natif, et la
      // navigation dans la liste reste sur les fleches haut et bas, qui est le
      // geste attendu d'une liste de suggestions.

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        /*
         * DIVERGENCE IXEZED, 16/08/2026 — la touche doit venir du CHAMP.
         *
         * Ce gestionnaire est pose sur le FORMULAIRE : l'evenement remonte donc
         * depuis n'importe quel descendant. Sans ce garde-fou, « Entree » sur la
         * croix de fermeture, sur « Effacer » ou sur la croix de vidage lancait
         * une navigation — vers « /search?q= » (page de resultats vide) dans les
         * deux premiers cas, vers une fiche produit dans le troisieme, quand la
         * recherche ne renvoyait qu'un seul resultat.
         *
         * Reproduit trois fois sur trois, aux trois formats : le panneau n'etait
         * pas utilisable au clavier.
         */
        if (event.target !== this.refs.searchInput) return;

        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }

    /*
     * DIVERGENCE IXEZED, 16/08/2026 — le focus revient au champ.
     * Le bloc retire emportait le bouton actif : plus rien n'etait au focus, et
     * il fallait re-parcourir tout le panneau au clavier pour revenir.
     */
    this.refs.searchInput?.focus();
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    /*
     * DIVERGENCE IXEZED, 16/08/2026 — le vidage programmatique passe quand meme.
     * Le garde-fou d'origine ignorait tout evenement sans « inputType », y
     * compris un champ vide par un remplissage automatique : le champ se
     * retrouvait vide avec les resultats de la requete precedente encore
     * affiches. On ne laisse passer que ce cas-la, le champ vide.
     */
    if (!event.inputType && this.refs.searchInput.value.trim() !== '') return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  /*
   * DIVERGENCE IXEZED, 16/08/2026 — attente portee de 200 a 350 ms.
   * 200 ms est plus court que l'intervalle d'un dactylographe ordinaire : un mot
   * de dix lettres tape a 260 ms partait en DIX requetes. A 350 ms, la frappe
   * courante est regroupee. Mesure du 16/08/2026.
   */
}, 350);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    /*
     * LA RECHERCHE ETAIT AVEUGLE AU FRANCAIS — corrige le 21/08/2026.
     *
     * Trouve par l'audit, confirme en ligne : le panneau de la loupe repondait
     * « Aucun resultat » a « roue », « gyroroue », « selle », « garde-boue »,
     * alors que la page /search trouvait 1 a 3 produits pour chacun. Il n'etait
     * pas casse : il trouvait tres bien « lynx », « mudguard », « pads ».
     *
     * LA CAUSE : sans « fields », Shopify applique son defaut pour la recherche
     * predictive — titre, type, variante, fournisseur — et JAMAIS la
     * description. Or les six titres de produits sont en anglais et les
     * descriptions en francais : un visiteur francais ne pouvait donc rien
     * trouver depuis la loupe, qui est la porte d'entree du site.
     *
     * MESURE AVANT / APRES sur l'API, a mots identiques :
     *   garde-boue   0 -> 1     roue   0 -> 3     selle   0 -> 1
     *
     * « body » ouvre la description, « tag » ouvre les etiquettes — de quoi
     * ajouter demain des mots-cles francais sans toucher au code.
     */
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,tag,body'
    );

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      /*
       * DIVERGENCE IXEZED, 16/08/2026 — le signal d'annulation est TRANSMIS.
       * Il etait cree a chaque frappe, mais jamais passe au chargement : la
       * requete precedente allait au bout, et « signal.aborted » n'etait
       * consulte qu'APRES l'arrivee de la reponse. L'annulation etait
       * decorative. Mesure : 19 allers-retours pour un mot tape puis efface,
       * aucun annule.
       */
      .getSectionHTML(this.dataset.sectionId, false, url, abortController.signal)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        // Le balisage vient d'etre remplace : les identifiants et l'etat du
        // champ sont a reposer (defaut F7).
        this.#majSemantique();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    /*
     * « RECEMMENT CONSULTE(S) » RETIRE — demande du client, 21/08/2026 :
     * « je veux que tu enleves les recemment consultes ».
     *
     * On coupe a LA SOURCE plutot que de masquer en CSS : masquer laisserait
     * la requete partir vers le serveur, le balisage s'injecter et le bouton
     * « Effacer » exister sous le voile. Ici rien n'est demande, rien n'est
     * insere — le panneau s'ouvre plus vite, et « Produits » redevient le seul
     * bloc. Cela clot du meme coup l'arbitrage F20 du 17/08 sur la
     * deduplication entre les deux blocs : il n'y a plus qu'un bloc.
     *
     * OU IL FALLAIT COUPER, ET OU IL NE FALLAIT PAS. Mon premier jet renvoyait
     * « null » depuis « #getRecentlyViewedProductsMarkup ». C'etait faux : le
     * garde ci-dessous fait « if (!recentlyViewedMarkup) return », et ce
     * « return » sort de la fonction ENTIERE. Pour tout visiteur ayant deja vu
     * un produit, l'etat vide n'aurait plus jamais ete rendu — panneau vide.
     * On neutralise donc la CONDITION, pas la fonction qu'elle appelle.
     *
     * POUR REVENIR EN ARRIERE : remettre « viewedProducts.length > 0 ». Tout
     * le mecanisme d'origine est intact, et la collecte des produits consultes
     * continue de vivre dans « recently-viewed-products ».
     */
    const viewedProducts = RecentlyViewed.getProducts();
    const AFFICHER_RECEMMENT_CONSULTES = false;

    if (AFFICHER_RECEMMENT_CONSULTES && viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);

      /*
       * F20 — ARBITRE PAR LE CLIENT LE 17/08/2026 : ON NE DEDUPLIQUE PAS.
       *
       * L'audit relevait qu'un produit peut paraitre deux fois a l'ouverture,
       * dans « Recemment consulte(s) » puis dans « Produits », et y voyait une
       * entorse a la regle « une information une seule fois par ecran ». J'ai
       * donc retire du bloc du bas ce que le bloc du haut montrait deja.
       *
       * C'ETAIT UNE ERREUR DE JUGEMENT, et le client l'a vue tout de suite :
       * « le deuxieme produit est affiche dans recemment consulte, et en bas il
       * n'en reste qu'un, alors qu'il est cense y etre ». « Produits », c'est
       * LE CATALOGUE : l'amputer le rend faux. Un historique et un catalogue ne
       * disent pas la meme chose — l'un repond « ou en etiez-vous », l'autre
       * « qu'est-ce qui existe ». La regle du « une seule fois » vise deux
       * affichages de la MEME information, pas deux roles differents.
       *
       * Elle avait en plus un defaut de construction : les deux blocs n'ont pas
       * la meme duree de vie. Effacer l'historique retirait son bloc, mais les
       * cartes supprimees dans l'autre ne revenaient pas — panneau a une seule
       * carte, voire vide, jusqu'au rechargement. C'est ce que le client avait
       * signale juste avant.
       *
       * Ne pas la remettre. Le controle « F20 bis » de tools/controle-recherche
       * verifie que le catalogue reste entier apres un « Effacer ».
       */
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    this.#majSemantique();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
